import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { filter, map } from 'rxjs/operators';
import { CustomNumberPipe } from '../../pipes/custom-number.pipe';
import { DynamicPipePipes } from '../../pipes/dynamic.pipe';
import { FormRangeNumberModel } from './form-range-number';
import { FormRangeNumberUtils } from './form-range-number.utils';
import { RangeSliderValue } from './range-slider';

const FORM_INPUT_CONTROL_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => FormRangeNumberComponent),
    multi: true
};

/**
 * Eine Komponente, die stellt einen Nummer-Schieberegler dart.
 */
@Component({
    selector: 'common-form-range-number',
    template: `
        <form [formGroup]="group">
            <div class="rangeSliderLabelTop">
                <div class="rangeSliderLabelLeft">{{(model ? model.from : min) | dynamic:pipes}}</div>
                <div class="rangeSliderLabelRight">{{(model ? model.to : max) | dynamic:pipes}}</div>
            </div>
            <common-range-slider formControlName="value" (sliderMove)="onSliderMove($event)"></common-range-slider>
            <div class="rangeSliderLabelBottom">
                <div class="rangeSliderLabelLeft">{{min | dynamic:pipes}}</div>
                <div class="rangeSliderLabelRight">{{max | dynamic:pipes}}</div>
            </div>
        </form>
    `,
    providers: [FORM_INPUT_CONTROL_VALUE_ACCESSOR]
})
export class FormRangeNumberComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {

    @Input() min: number = 0;

    @Input() max: number = 100;

    @Input() fraction: number = 0; // Stellen nach dem Komma

    @Input() pipes: DynamicPipePipes[];

    model: FormRangeNumberModel;

    group: UntypedFormGroup;

    private change: EventEmitter<FormRangeNumberModel> = new EventEmitter<FormRangeNumberModel>();

    private touch: EventEmitter<FormRangeNumberModel> = new EventEmitter<FormRangeNumberModel>();

    constructor(private fb: UntypedFormBuilder, numberPipe: CustomNumberPipe) {
        this.group = this.fb.group({
            value: []
        });
        this.pipes = [[numberPipe, []]];
    }

    ngOnInit() {
        this.registerFormChange();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.hasOwnProperty('min') || changes.hasOwnProperty('max') && this.model) {
            this.writeValue(FormRangeNumberUtils.prepareModel(this.model, this.min, this.max));
        }
    }

    ngOnDestroy() {
        this.change.complete();
        this.touch.complete();
    }

    writeValue(obj): void {
        if (!obj) {
            this.group.reset();
            this.model = undefined;
            return;
        }
        this.model = FormRangeNumberUtils.prepareModel(obj, this.min, this.max);
        this.group.controls['value']
            .patchValue(FormRangeNumberUtils.numberToSliderValue(this.model, this.min, this.max), {
                onlySelf: true,
                emitEvent: false
            });
    }

    registerOnChange(fn: any): void {
        this.change.subscribe(fn);
    }

    registerOnTouched(fn: any): void {
        this.touch.subscribe(fn);
    }

    setDisabledState(isDisabled: boolean) {
        if (isDisabled) {
            return this.group.disable({emitEvent: false});
        }
        this.group.enable({emitEvent: false});
    }

    onSliderMove(value: RangeSliderValue) {
        this.model = FormRangeNumberUtils.sliderValueToNumber(value, this.min, this.max, this.fraction);
    }

    private registerFormChange() {
        this.group.valueChanges
            .pipe(
                map(x => x['value']),
                filter(x => x && x.from !== undefined && x.from !== null && x.to !== undefined && x.to !== null),
                map(x => FormRangeNumberUtils.sliderValueToNumber(x, this.min, this.max, this.fraction))
            )
            .subscribe(x => this.change.emit(x));
    }
}
